// pages/GuildFinance.js (새로 생성 - 길드 회계 관리)
import React, { useState, useEffect } from "react";
import { guildDataService } from "./GuildDataService";

function GuildFinance({ guildId, currentUser, guild, canEdit }) {
  const [treasury, setTreasury] = useState({ 
    initialAmount: 0, 
    currentAmount: 0, 
    records: [] 
  });
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [guildMembers, setGuildMembers] = useState([]);
  
  // 새 기록 추가 폼 상태
  const [newRecord, setNewRecord] = useState({
    type: "expense", // "income" or "expense"
    amount: "",
    purpose: "",
    participants: []
  });
  
  // 필터링 및 정렬 상태
  const [filter, setFilter] = useState("all"); // "all", "income", "expense"
  const [sortOrder, setSortOrder] = useState("desc"); // "desc", "asc"
  const [currentPage, setCurrentPage] = useState(0);
  const RECORDS_PER_PAGE = 10;

  useEffect(() => {
    if (guildId) {
      loadTreasuryData();
      loadGuildMembers();
    }
    // eslint-disable-next-line
  }, [guildId]);

  const loadTreasuryData = async () => {
    setLoading(true);
    try {
      const guildData = await guildDataService.getMyGuild(currentUser.id);
      if (guildData?.guild?.treasury) {
        setTreasury(guildData.guild.treasury);
      }
    } catch (error) {
      console.error("회계 데이터 로드 실패:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadGuildMembers = async () => {
    try {
      const guildData = await guildDataService.getMyGuild(currentUser.id);
      if (guildData?.members) {
        setGuildMembers(guildData.members);
      }
    } catch (error) {
      console.error("멤버 로드 실패:", error);
    }
  };

  const handleAddRecord = async () => {
    if (!newRecord.amount || !newRecord.purpose) {
      alert("금액과 목적을 입력해주세요.");
      return;
    }

    if (isNaN(newRecord.amount) || Number(newRecord.amount) <= 0) {
      alert("올바른 금액을 입력해주세요.");
      return;
    }

    try {
      await guildDataService.addTreasuryRecord(guildId, {
        ...newRecord,
        amount: Number(newRecord.amount),
        addedBy: currentUser.nickname
      });
      
      // 폼 초기화
      setNewRecord({
        type: "expense",
        amount: "",
        purpose: "",
        participants: []
      });
      setShowAddForm(false);
      
      // 데이터 새로고침
      await loadTreasuryData();
      alert("기록이 추가되었습니다!");
    } catch (error) {
      console.error("기록 추가 실패:", error);
      alert("기록 추가에 실패했습니다.");
    }
  };

  const toggleParticipant = (memberId) => {
    setNewRecord(prev => ({
      ...prev,
      participants: prev.participants.includes(memberId)
        ? prev.participants.filter(id => id !== memberId)
        : [...prev.participants, memberId]
    }));
  };

  // 필터링된 기록들
  const getFilteredRecords = () => {
    let filtered = treasury.records || [];
    
    if (filter !== "all") {
      filtered = filtered.filter(record => record.type === filter);
    }
    
    // 정렬
    filtered.sort((a, b) => {
      const aDate = new Date(a.date);
      const bDate = new Date(b.date);
      return sortOrder === "desc" ? bDate - aDate : aDate - bDate;
    });
    
    return filtered;
  };

  const filteredRecords = getFilteredRecords();
  const totalPages = Math.ceil(filteredRecords.length / RECORDS_PER_PAGE);
  const currentPageRecords = filteredRecords.slice(
    currentPage * RECORDS_PER_PAGE,
    (currentPage + 1) * RECORDS_PER_PAGE
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount) + '원';
  };

  const getParticipantNames = (participantIds) => {
    if (!participantIds || participantIds.length === 0) return "전체";
    return participantIds
      .map(id => guildMembers.find(m => m.id === id)?.nickname || "알 수 없음")
      .join(", ");
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>회계 정보를 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* 회계 현황 요약 */}
      <div style={styles.summarySection}>
        <div style={styles.summaryCard} className="glass-morphism">
          <h3 style={styles.summaryTitle}>💰 길드 회계 현황</h3>
          <div style={styles.summaryGrid}>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>초기 금액</span>
              <span style={styles.summaryValue}>
                {formatCurrency(treasury.initialAmount)}
              </span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>현재 잔액</span>
              <span style={{
                ...styles.summaryValue,
                color: treasury.currentAmount >= 0 ? "#4ecdc4" : "#1f2937"
              }}>
                {formatCurrency(treasury.currentAmount)}
              </span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>총 수입</span>
              <span style={{...styles.summaryValue, color: "#4ecdc4"}}>
                {formatCurrency(
                  (treasury.records || [])
                    .filter(r => r.type === "income")
                    .reduce((sum, r) => sum + r.amount, 0)
                )}
              </span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>총 지출</span>
              <span style={{...styles.summaryValue, color: "#1f2937"}}>
                {formatCurrency(
                  (treasury.records || [])
                    .filter(r => r.type === "expense")
                    .reduce((sum, r) => sum + r.amount, 0)
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 기록 관리 섹션 */}
      <div style={styles.recordsSection}>
        <div style={styles.recordsHeader}>
          <h3 style={styles.sectionTitle}>💳 회계 기록</h3>
          
          {canEdit && (
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              style={styles.addButton}
              className="neon-glow"
            >
              {showAddForm ? "취소" : "기록 추가"}
            </button>
          )}
        </div>

        {/* 새 기록 추가 폼 */}
        {showAddForm && canEdit && (
          <div style={styles.addForm} className="glass-morphism">
            <h4 style={styles.formTitle}>새 회계 기록 추가</h4>
            
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>구분</label>
                <select
                  value={newRecord.type}
                  onChange={(e) => setNewRecord(prev => ({...prev, type: e.target.value}))}
                  style={styles.formSelect}
                >
                  <option value="expense">지출</option>
                  <option value="income">수입</option>
                </select>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>금액 (원)</label>
                <input
                  type="number"
                  value={newRecord.amount}
                  onChange={(e) => setNewRecord(prev => ({...prev, amount: e.target.value}))}
                  style={styles.formInput}
                  placeholder="0"
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>목적/내용</label>
              <input
                type="text"
                value={newRecord.purpose}
                onChange={(e) => setNewRecord(prev => ({...prev, purpose: e.target.value}))}
                style={styles.formInput}
                placeholder="예: 장비 구입, 대회 참가비, 회식비 등"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>참가 인원 (선택)</label>
              <div style={styles.participantGrid}>
                {guildMembers.map(member => (
                  <label key={member.id} style={styles.participantItem}>
                    <input
                      type="checkbox"
                      checked={newRecord.participants.includes(member.id)}
                      onChange={() => toggleParticipant(member.id)}
                      style={styles.checkbox}
                    />
                    <span style={styles.participantName}>{member.nickname}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={styles.formActions}>
              <button
                onClick={handleAddRecord}
                style={styles.submitButton}
                className="neon-glow"
              >
                기록 추가
              </button>
            </div>
          </div>
        )}

        {/* 필터 및 정렬 */}
        <div style={styles.filterSection} className="glass-morphism">
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>필터:</label>
            <select
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setCurrentPage(0);
              }}
              style={styles.filterSelect}
            >
              <option value="all">전체</option>
              <option value="income">수입</option>
              <option value="expense">지출</option>
            </select>
          </div>
          
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>정렬:</label>
            <select
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value);
                setCurrentPage(0);
              }}
              style={styles.filterSelect}
            >
              <option value="desc">최신순</option>
              <option value="asc">오래된순</option>
            </select>
          </div>
        </div>

        {/* 기록 목록 */}
        <div style={styles.recordsList}>
          {currentPageRecords.length === 0 ? (
            <div style={styles.emptyState} className="glass-morphism">
              <div style={styles.emptyIcon}>📋</div>
              <p style={styles.emptyText}>아직 회계 기록이 없습니다.</p>
            </div>
          ) : (
            currentPageRecords.map((record, index) => (
              <div key={record.id || index} style={styles.recordCard} className="glass-morphism">
                <div style={styles.recordHeader}>
                  <div style={styles.recordType}>
                    <span style={{
                      ...styles.typeTag,
                      backgroundColor: record.type === "income" ? "#4ecdc4" : "#1f2937"
                    }}>
                      {record.type === "income" ? "수입" : "지출"}
                    </span>
                    <span style={styles.recordDate}>
                      {new Date(record.date).toLocaleDateString()}
                    </span>
                  </div>
                  <span style={{
                    ...styles.recordAmount,
                    color: record.type === "income" ? "#4ecdc4" : "#1f2937"
                  }}>
                    {record.type === "income" ? "+" : "-"}{formatCurrency(record.amount)}
                  </span>
                </div>
                
                <div style={styles.recordBody}>
                  <p style={styles.recordPurpose}>{record.purpose}</p>
                  <div style={styles.recordMeta}>
                    <span style={styles.recordParticipants}>
                      참여: {getParticipantNames(record.participants)}
                    </span>
                    <span style={styles.recordAddedBy}>
                      작성자: {record.addedBy}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* 페이징 */}
        {totalPages > 1 && (
          <div style={styles.pagination}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              style={styles.pageButton}
            >
              이전
            </button>
            <span style={styles.pageInfo}>
              {currentPage + 1} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
              disabled={currentPage === totalPages - 1}
              style={styles.pageButton}
            >
              다음
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    color: "black",
    maxWidth: 1000,
    margin: "0 auto"
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
    color: "black"
  },
  spinner: {
    width: 40,
    height: 40,
    border: "4px solid rgba(31,41,55,0.7)",
    borderTop: "4px solid black",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },
  loadingText: {
    marginTop: 15,
    fontSize: 16
  },
  
  // 요약 섹션
  summarySection: {
    marginBottom: 30
  },
  summaryCard: {
    padding: 25,
    borderRadius: 15
  },
  summaryTitle: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 20,
    textAlign: "center"
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 15
  },
  summaryItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 15,
    background: 'rgba(255,255,255,0.7)',
    borderRadius: 10
  },
  summaryLabel: {
    fontSize: 14,
    opacity: 0.8,
    marginBottom: 5
  },
  summaryValue: {
    fontSize: 18,
    fontWeight: 700,
    color: "black"
  },

  // 기록 섹션
  recordsSection: {
    color: "black"
  },
  recordsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 700,
    margin: 0
  },
  addButton: {
    background: "linear-gradient(45deg, #4ecdc4, #44a08d)",
    border: "none",
    color: "black",
    padding: "10px 20px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600
  },

  // 추가 폼
  addForm: {
    padding: 25,
    borderRadius: 15,
    marginBottom: 20
  },
  formTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 20,
    textAlign: "center"
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 15,
    marginBottom: 15
  },
  formGroup: {
    marginBottom: 15
  },
  formLabel: {
    display: "block",
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 5,
    opacity: 0.9
  },
  formInput: {
    width: "100%",
    padding: 12,
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.3)",
    background: 'rgba(255,255,255,0.7)',
    color: "black",
    fontSize: 14,
    boxSizing: "border-box"
  },
  formSelect: {
    width: "100%",
    padding: 12,
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.3)",
    background: 'rgba(255,255,255,0.7)',
    color: "black",
    fontSize: 14,
    boxSizing: "border-box"
  },
  participantGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 10,
    maxHeight: 150,
    overflowY: "auto",
    padding: 10,
    background: 'rgba(255,255,255,0.7)',
    borderRadius: 8
  },
  participantItem: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    fontSize: 14
  },
  checkbox: {
    marginRight: 8
  },
  participantName: {
    opacity: 0.9
  },
  formActions: {
    textAlign: "center",
    marginTop: 20
  },
  submitButton: {
    background: "linear-gradient(45deg, #00d4ff, #0099cc)",
    border: "none",
    color: "black",
    padding: "12px 30px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 16,
    fontWeight: 600
  },

  // 필터 섹션
  filterSection: {
    display: "flex",
    gap: 20,
    padding: 15,
    borderRadius: 10,
    marginBottom: 20
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: 8
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: 600
  },
  filterSelect: {
    padding: 8,
    borderRadius: 5,
    border: "1px solid rgba(255,255,255,0.3)",
    background: 'rgba(255,255,255,0.7)',
    color: "black",
    fontSize: 14
  },

  // 기록 목록
  recordsList: {
    marginBottom: 20
  },
  emptyState: {
    textAlign: "center",
    padding: 40,
    borderRadius: 15
  },
  emptyIcon: {
    fontSize: 50,
    marginBottom: 15
  },
  emptyText: {
    fontSize: 16,
    opacity: 0.7
  },
  recordCard: {
    padding: 20,
    borderRadius: 12,
    marginBottom: 15
  },
  recordHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 15
  },
  recordType: {
    display: "flex",
    alignItems: "center",
    gap: 10
  },
  typeTag: {
    padding: "4px 8px",
    borderRadius: 5,
    fontSize: 12,
    fontWeight: 600,
    color: "black"
  },
  recordDate: {
    fontSize: 14,
    opacity: 0.7
  },
  recordAmount: {
    fontSize: 18,
    fontWeight: 700
  },
  recordBody: {
    borderTop: "1px solid rgba(255,255,255,0.1)",
    paddingTop: 15
  },
  recordPurpose: {
    fontSize: 16,
    marginBottom: 10,
    lineHeight: 1.4
  },
  recordMeta: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 12,
    opacity: 0.7
  },
  recordParticipants: {},
  recordAddedBy: {},

  // 페이징
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 15,
    marginTop: 20
  },
  pageButton: {
    background: 'rgba(255,255,255,0.7)',
    border: "none",
    color: "black",
    padding: "8px 16px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14
  },
  pageInfo: {
    fontSize: 14,
    fontWeight: 600
  }
};

export default GuildFinance;